import React, { RefObject, useLayoutEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import i18next from 'i18next';
import { ThemePrepared } from '@fluentui/react-northstar';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { getDropdownBoxShadow } from '../../../tikit/ticketHelper';
import Styles from '../NestedDropdown/NestedDropdown.module.css';
import { CategoryOption, rowLabel } from './inlineCategoryOptions';

const LIST_MAX_HEIGHT = 300;
/** A tree row's indent per level, on top of its own left padding. */
const TREE_INDENT = 12;

/** The rows' own side padding (px-4), plus room for the list's scrollbar and its 1px edges. */
const ROW_CHROME = 16 * 2 + 12 + 2;
const EDGE = 8;
/**
 * The widest a list grows past its box: deep category paths ("Hardware > Computer > Desktop > Standard > Tier 5 >
 * Tier 6 > Tier 7") made it ~900px, spreading over the ticket and off the window (Shaun, 2026-10-19). A path longer
 * than this shows its end - the part being chosen - with the full path on hover.
 */
const LIST_MAX_WIDTH = 480;

/** Whether an event's path runs through one of these lists - a panel that hosts one counts that as inside. */
export const isInTypeaheadList = (path: EventTarget[]) =>
  path.some(target => (target as HTMLElement)?.dataset?.inlineTypeahead === 'true');

/**
 * Scrolls the list - and only the list - so a row is in view. Not scrollIntoView: that also scrolls every
 * scrollable ancestor, and the app's overflow-hidden page shell is one (see FluentUi CLAUDE.md).
 */
export const revealRow = (list: HTMLElement | null, index: number) => {
  const row = list?.querySelector<HTMLElement>(`[data-index="${index}"]`);
  if (!list || !row) return;
  if (row.offsetTop < list.scrollTop) list.scrollTop = row.offsetTop;
  else if (row.offsetTop + row.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = row.offsetTop + row.offsetHeight - list.clientHeight;
};

/** `above`: `top` is where the list ends, and it is drawn up from there. */
export interface ListPosition { left: number; width: number; top: number; above?: boolean; maxHeight: number; }

/**
 * The visible left and right of the view an element sits in: the window, cut down by every ancestor that
 * SCROLLS - the grid's scroller, beside the side menu - to inside its scrollbar. A list or panel slides to stay inside these, so
 * a cell scrolled half out of the grid never pulls its list over the menu. Only scrollers count: the grid cell
 * clips too (overflow hidden), and counting it squeezed the list down to the cell's width.
 */
export const viewBounds = (element: HTMLElement): { left: number; right: number } => {
  let left = 0;
  let right = window.innerWidth;
  for (let ancestor = element.parentElement; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(ancestor);
    const scrolls = (overflow: string) => overflow === 'auto' || overflow === 'scroll';
    if (!scrolls(overflowX) && !scrolls(overflowY)) continue;
    // The inside edges (clientLeft / clientWidth), so a panel or list never covers the scroller's own up-and-down
    // scrollbar at its right (Shaun, 2026-10-19) - as viewBoundsY keeps them above the sideways one.
    const rect = ancestor.getBoundingClientRect();
    left = Math.max(left, rect.left + ancestor.clientLeft);
    right = Math.min(right, rect.left + ancestor.clientLeft + ancestor.clientWidth);
  }
  return { left, right };
};

/**
 * The bottom edge of the frozen column headers over the list an element sits in, or null where the headers are not
 * frozen (.frozen-list-header in main.css). What lies above it is hidden under the headers.
 */
export const frozenHeaderBottom = (element: HTMLElement): number | null => {
  const header = element.closest('.ms-DetailsList')?.querySelector<HTMLElement>('.ms-DetailsList-headerWrapper');
  return header && getComputedStyle(header).position === 'sticky' ? header.getBoundingClientRect().bottom : null;
};

/**
 * The visible top and bottom of the view an element sits in: the window, cut down by every ancestor that SCROLLS
 * up and down - the grid's scroller, under the toolbar - and by the list's frozen headers. Its bottom is above its
 * own scrollbar (clientHeight), so a panel kept inside it never covers the bar.
 */
export const viewBoundsY = (element: HTMLElement): { top: number; bottom: number } => {
  let top = 0;
  let bottom = window.innerHeight;
  for (let ancestor = element.parentElement; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
    const overflowY = getComputedStyle(ancestor).overflowY;
    if (overflowY !== 'auto' && overflowY !== 'scroll') continue;
    const rect = ancestor.getBoundingClientRect();
    top = Math.max(top, rect.top + ancestor.clientTop);
    bottom = Math.min(bottom, rect.top + ancestor.clientTop + ancestor.clientHeight);
  }
  const headerBottom = frozenHeaderBottom(element);
  if (headerBottom != null) top = Math.max(top, headerBottom);
  return { top, bottom };
};

/**
 * Anchored to the element's left edge where it can be, slid in to stay within the view. Only the position
 * moves - never the width; a view narrower than the list keeps the list's left edge inside it.
 */
const anchoredLeft = (anchorLeft: number, width: number, bounds: { left: number; right: number }) =>
  Math.max(bounds.left + VIEW_INSET, Math.min(anchorLeft, bounds.right - width - VIEW_INSET));

const VIEW_INSET = 4;

/** Follows the anchor while anything outside the floating element scrolls, and on resize. */
const followAnchor = (floating: RefObject<HTMLElement>, place: () => void) => {
  const onScroll = (event: Event) => {
    const target = event.target as Node;
    if (floating.current?.contains(target)) return;
    if ((target as HTMLElement)?.dataset?.inlineTypeahead === 'true') return;
    place();
  };
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', place);
  return () => {
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', place);
  };
};

/**
 * Where an edit panel goes: under its cell (above when there is no room), anchored to the cell while it is in
 * view and following it as the grid scrolls either way, kept within the view on all four sides - a cell scrolled
 * out of the grid leaves its panel at the grid's edge, not over the toolbar or the scrollbar (Shaun, 2026-10-14).
 * Placed by its measured height (roomBelowWanted until it has one), and placed again when that changes - chips
 * wrap onto a new line.
 */
export const usePanelPlacement = (
  cellRef: RefObject<HTMLElement>,
  panelRef: RefObject<HTMLElement>,
  open: boolean,
  width: number,
  roomBelowWanted: number
): { left: number; top?: number; bottom?: number } | null => {
  const [position, setPosition] = useState<{ left: number; top?: number; bottom?: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) { setPosition(null); return; }
    let observed: HTMLElement | null = null;
    const resized = new ResizeObserver(() => place());
    const place = () => {
      const cell = cellRef.current;
      if (!cell) return;
      const panel = panelRef.current;
      if (panel && panel !== observed) { resized.observe(panel); observed = panel; }
      const rect = cell.getBoundingClientRect();
      const left = anchoredLeft(rect.left, width, viewBounds(cell));
      const bounds = viewBoundsY(cell);
      const height = panel?.offsetHeight || roomBelowWanted;
      const below = bounds.bottom - rect.bottom;
      const above = rect.top - bounds.top;
      const wanted = below >= height + 2 || below >= above ? rect.bottom + 2 : rect.top - 2 - height;
      const top = Math.max(bounds.top + VIEW_INSET, Math.min(wanted, bounds.bottom - height - VIEW_INSET));
      setPosition(current => (current?.left === left && current?.top === top ? current : { left, top }));
    };
    place();
    const unfollow = followAnchor(panelRef, place);
    // The panel itself appears a render after its first position - placed again once it has a height.
    const frame = requestAnimationFrame(place);
    return () => { unfollow(); resized.disconnect(); cancelAnimationFrame(frame); };
  }, [open, width]);
  return position;
};

let measureContext: CanvasRenderingContext2D | null | undefined;

/**
 * The type a list draws in: the size of the row it opened from under a grid cell (12px), the ticket page's
 * 14px in a panel, whose boxes are 14px too.
 */
export type ListSize = 12 | 14;

/** The widest of the names as the list draws them - semibold, since the chosen row is bold. */
/** The widest row as the list draws it - semibold, since the chosen row is bold - with a tree row's indent. */
const widestName = (names: string[], fontFamily: string, size: ListSize, indents?: number[]): number => {
  measureContext ??= document.createElement('canvas').getContext('2d');
  if (!measureContext) return 0;
  measureContext.font = `600 ${size}px ${fontFamily}`;
  return names.reduce((widest, name, i) => Math.max(widest, measureContext.measureText(name).width + (indents?.[i] ?? 0) * TREE_INDENT), 0);
};

/**
 * Where the list goes and how wide it is: under its box (or above it when there is no room below), as wide as
 * its longest option needs and never narrower than the box, nor wider than the window. Follows the box while
 * anything scrolls, except the list itself.
 */
export const useListPlacement = (
  anchorRef: RefObject<HTMLElement>,
  listRef: RefObject<HTMLElement>,
  open: boolean,
  names: string[],
  size: ListSize = 14,
  /** As wide as the box and in line with it, as a form's dropdowns are - not widened to its longest option. */
  fitBox = false,
  /** Each name's tree indent in levels, while the list shows the tree (listWidthRows). */
  indents?: number[]
): ListPosition | null => {
  const [position, setPosition] = useState<ListPosition | null>(null);
  const namesKey = names.join('\n') + (indents ? `|${indents.join(',')}` : '');

  useLayoutEffect(() => {
    if (!open) { setPosition(null); return; }
    const anchor = anchorRef.current;
    if (!anchor) return;
    const fontFamily = getComputedStyle(anchor).fontFamily;
    const needed = Math.ceil(widestName(names, fontFamily, size, indents)) + ROW_CHROME;

    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const bounds = viewBounds(anchor);
      // As wide as its longest option, up to LIST_MAX_WIDTH (or the box, if wider), and never wider than the view it
      // sits in - the ticket page's properties pane is ~430px, and a wider list ran off the window's edge.
      const width = fitBox ? rect.width : Math.min(Math.max(rect.width, needed), Math.max(rect.width, LIST_MAX_WIDTH), window.innerWidth - EDGE * 2,
        Math.max(rect.width, bounds.right - bounds.left - VIEW_INSET * 2));
      const left = fitBox ? rect.left : anchoredLeft(rect.left, width, bounds);
      // Kept inside the view up and down too, as the edit panels are (usePanelPlacement): under the frozen headers and
      // above the scrollbar, waiting at the edge when its cell scrolls out - it rode up over the toolbar (Shaun).
      const boundsY = viewBoundsY(anchor);
      // With the iPhone keyboard up Safari moves the visible part of the page (visualViewport.offsetTop), and measures
      // the box from there while a fixed list is still placed from the page's own top - so it sat that far above its
      // box (Shaun, 2026-10-21). Nothing on desktop, where the offset is 0.
      const shiftX = window.visualViewport?.offsetLeft ?? 0;
      const shiftY = window.visualViewport?.offsetTop ?? 0;
      const below = boundsY.bottom - rect.bottom - EDGE;
      const above = rect.top - boundsY.top - EDGE;
      if (below >= Math.min(LIST_MAX_HEIGHT, 200) || below >= above) {
        const top = Math.max(boundsY.top + VIEW_INSET, Math.min(rect.bottom + 2, boundsY.bottom - VIEW_INSET));
        setPosition({ left: left + shiftX, width, top: top + shiftY, maxHeight: Math.max(0, Math.min(LIST_MAX_HEIGHT, boundsY.bottom - VIEW_INSET - top)) });
      } else {
        const bottomEdge = Math.min(rect.top - 2, boundsY.bottom - VIEW_INSET);
        // Its top edge set and drawn upwards, not a `bottom` measured from innerHeight: with the iPhone keyboard up a
        // fixed element's bottom is not where the page's measurements put it, and the list floated far above its box.
        setPosition({ left: left + shiftX, width, top: bottomEdge + shiftY, above: true, maxHeight: Math.max(0, Math.min(LIST_MAX_HEIGHT, bottomEdge - boundsY.top - VIEW_INSET)) });
      }
    };
    place();
    const unfollow = followAnchor(listRef, place);
    // The box can change height under an open list - a chip added or taken off wraps the chips onto another line -
    // and the list must move with its bottom edge rather than stay where the box used to end (Shaun, 2026-10-13).
    const resized = new ResizeObserver(place);
    resized.observe(anchor);
    // The keyboard coming up or going down moves that visible part of the page.
    window.visualViewport?.addEventListener('resize', place);
    window.visualViewport?.addEventListener('scroll', place);
    return () => {
      unfollow();
      resized.disconnect();
      window.visualViewport?.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('scroll', place);
    };
  }, [open, namesKey, size, fitBox]);

  return position;
};

interface Props {
  listRef: RefObject<HTMLDivElement>;
  position: ListPosition;
  shown: CategoryOption[];
  active: number;
  /** The option the box holds - drawn bold. */
  selectedId: number | null;
  /** What has been typed - drawn bold inside each option. */
  filter: string;
  globalTheme: ThemePrepared;
  onPick: (option: CategoryOption) => void;
  onHover: (index: number) => void;
  size?: ListSize;
}

/** Puts a row in the middle of the list - for opening, where the agent wants to see what is around it. */
const centerRow = (list: HTMLElement | null, index: number) => {
  const row = list?.querySelector<HTMLElement>(`[data-index="${index}"]`);
  if (!list || !row) return;
  // Where each row starts, measured rather than counted: rows are a fraction of a pixel taller than offsetHeight says.
  const listTop = list.getBoundingClientRect().top - list.scrollTop;
  const startOf = (element: Element) => element.getBoundingClientRect().top - listTop;
  const rowBox = row.getBoundingClientRect();
  const centred = startOf(row) + rowBox.height / 2 - list.clientHeight / 2;
  // Then moved to the nearest row's top, so the top row is never half cut - it read as missing, and hovering it
  // scrolled the list.
  const starts = Array.from(list.querySelectorAll('[data-index]'), startOf);
  const nearest = starts.reduce((best, start) => Math.abs(start - centred) < Math.abs(best - centred) ? start : best, 0);
  list.scrollTop = Math.max(0, nearest);
};

/**
 * The suggestion list under a typable box, drawn on the body so nothing clips it. A press on it never takes
 * focus from the box, so the box only loses focus when the agent really leaves.
 */
export const TypeaheadList = ({ listRef, position, shown, active, selectedId, filter, globalTheme, onPick, onHover, size = 14 }: Props) => {
  const { colorScheme } = globalTheme.siteVariables;
  // The list opens already scrolled to its lit row, centred: done before the first paint, so it never shows
  // at the top and then jumps.
  useLayoutEffect(() => {
    if (active >= 0) centerRow(listRef.current, active);
  }, []);
  return ReactDOM.createPortal(
    <div
      ref={listRef}
      role="listbox"
      data-inline-typeahead="true"
      className={`${Styles.OptionContainer} rounded overflow-auto`}
      onMouseDown={event => event.preventDefault()}
      style={{
        position: 'fixed',
        left: position.left,
        width: position.width,
        maxHeight: position.maxHeight,
        top: position.top,
        ...(position.above ? { transform: 'translateY(-100%)' } : {}),
        // Above Northstar's dialog overlay (9999999 in main.css), as its own pop-ups are (99999999): Bulk Edit's lists
        // drew behind its dialog.
        zIndex: 99999999,
        boxShadow: getDropdownBoxShadow(globalTheme),
        ...ThemeColorScheme(globalTheme.siteVariables)
      }}
    >
      {shown.length === 0
        ? <div className="px-4 py-1.5" style={{ fontSize: size, color: colorScheme.default.foreground2 }}>{i18next.t('ticket.ticket-list.no-matches')}</div>
        : shown.map((option, index) => {
          const highlighted = index === active ? Styles.ListItemHighlighted : '';
          const selected = option.id === selectedId ? `${Styles.ListItemActive} font-semibold` : '';
          const { indent, runs } = rowLabel(option, filter);
          return (
          <div
            key={option.id}
            role="option"
            data-index={index}
            aria-selected={option.id === selectedId}
            className={`${Styles.ListItem} ${highlighted} ${selected} px-4 py-1.5 cursor-pointer whitespace-nowrap`}
            title={option.name}
            // Too long for the list: cut from the start, so a path keeps its end - the part being chosen - in view
            // ("... > Tier 6 > Tier 7"). The bdi keeps the words in reading order inside the right-to-left box.
            style={{ fontSize: size, overflow: 'hidden', textOverflow: 'ellipsis', direction: 'rtl', textAlign: 'left', ...(indent ? { paddingLeft: 16 + indent * TREE_INDENT } : {}) }}
            onMouseDown={event => { event.preventDefault(); onPick(option); }}
            onMouseEnter={() => onHover(index)}
          >
            <bdi>
              {runs.map(run => {
                if (run.match) return <strong key={run.start} style={{ color: colorScheme.default.foreground }}>{run.text}</strong>;
                if (run.parent) return <span key={run.start} style={{ color: colorScheme.default.foreground2 }}>{run.text}</span>;
                return <React.Fragment key={run.start}>{run.text}</React.Fragment>;
              })}
            </bdi>
          </div>
          );
        })}
    </div>,
    document.body
  );
};
